import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";

export default function VerifyEmail() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const token = params.get("token") || "";
    const [state, setState] = useState(token ? "verifying" : "missing");
    const [error, setError] = useState("");
    const ranRef = useRef(false);

    useEffect(() => {
        if (!token || ranRef.current) return;
        ranRef.current = true;
        (async () => {
            try {
                await api.post("/auth/verify-email", { token });
                setState("success");
                if (typeof refresh === "function") {
                    try { await refresh(); } catch (_) { /* non-fatal */ }
                }
            } catch (err) {
                setError(formatApiError(err?.response?.data?.detail));
                setState("error");
            }
        })();
    }, [token, refresh]);

    return (
        <div className="min-h-screen bg-sand-50 text-ink flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md text-center">
                <div className="flex items-center justify-between">
                    <Link to="/dashboard" className="sp-overline" data-testid="verify-back">← Dashboard</Link>
                    <ThemeToggle />
                </div>

                {state === "missing" && (
                    <div className="mt-10" data-testid="verify-missing-token">
                        <h1 className="font-serif text-3xl">Missing token</h1>
                        <p className="text-ink-soft mt-2">
                            This verification link is incomplete. Please use the exact link from your email.
                        </p>
                    </div>
                )}

                {state === "verifying" && (
                    <div className="mt-10" data-testid="verify-loading">
                        <h1 className="font-serif text-3xl">Verifying your email…</h1>
                        <p className="text-ink-soft mt-2">Just a moment.</p>
                    </div>
                )}

                {state === "success" && (
                    <div className="mt-10" data-testid="verify-success">
                        <div className="sp-overline text-emerald-700">Email verified</div>
                        <h1 className="font-serif text-3xl mt-2">Welcome aboard.</h1>
                        <p className="text-ink-soft mt-3">
                            Your email is confirmed. All StarPath features are unlocked.
                        </p>
                        <button
                            className="sp-btn-primary mt-8"
                            onClick={() => navigate(user ? "/dashboard" : "/login", { replace: true })}
                            data-testid="verify-continue"
                        >
                            Continue
                        </button>
                    </div>
                )}

                {state === "error" && (
                    <div className="mt-10" data-testid="verify-error">
                        <h1 className="font-serif text-3xl">Link expired or invalid</h1>
                        <p className="text-ink-soft mt-2">{error || "This verification link can no longer be used."}</p>
                        <div className="mt-6 flex flex-col gap-3">
                            <ResendButton />
                            <Link to={user ? "/dashboard" : "/login"} className="sp-btn-ghost text-center" data-testid="verify-error-back">
                                {user ? "Back to dashboard" : "Back to sign in"}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ResendButton() {
    const { user } = useAuth();
    const [email, setEmail] = useState(user?.email || "");
    const [state, setState] = useState("idle"); // idle | busy | sent | cooldown
    const [msg, setMsg] = useState("");

    const send = async () => {
        setState("busy"); setMsg("");
        try {
            await api.post("/auth/verify-email/resend", { email });
            setState("sent");
            setMsg("If your email needs verification, a new link is on its way.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 429) {
                setState("cooldown");
                setMsg(formatApiError(detail));
            } else {
                setState("idle");
                setMsg(formatApiError(detail));
            }
        }
    };

    if (!user) {
        return (
            <div className="text-sm text-ink-muted">
                <Link to="/login" className="sp-link">Sign in</Link> to request a new verification email.
            </div>
        );
    }
    return (
        <div>
            <button
                onClick={send}
                disabled={state === "busy" || state === "cooldown"}
                className="sp-btn-primary w-full disabled:opacity-60"
                data-testid="verify-resend"
            >
                {state === "busy" ? "Sending…" : state === "sent" ? "Sent" : "Resend verification email"}
            </button>
            {msg && (
                <div
                    className={`mt-3 text-sm ${state === "cooldown" ? "text-amber-700" : "text-ink-muted"}`}
                    data-testid="verify-resend-msg"
                >
                    {msg}
                </div>
            )}
        </div>
    );
}
